import React, { useState, useEffect } from 'react';
import {
  Box,
  Chip,
  Autocomplete,
  TextField,
  Typography,
  Paper
} from '@mui/material';
import {
  LocalOffer as TagIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import logger from '../utils/logger';

/**
 * Tag Members Component
 *
 * Allows users to tag/mention specific family members on content.
 * Tagged members will receive notifications.
 *
 * Props:
 * - familyCode: String - The family code
 * - value: Array - Array of tagged member IDs
 * - onChange: Function - Called when tagged members change
 * - label: String - Label for the control (optional)
 * - helperText: String - Helper text (optional)
 */
const TagMembers = ({
  familyCode,
  value = [],
  onChange,
  label = 'Tag Family Members',
  helperText = 'Tag members to notify them about this post'
}) => {
  const [taggedMembers, setTaggedMembers] = useState(value || []);
  const [familyMembers, setFamilyMembers] = useState([]);
  const [loading, setLoading] = useState(false);

  // Load family members when component mounts or familyCode changes
  useEffect(() => {
    if (!familyCode) return;

    const loadFamilyMembers = async () => {
      setLoading(true);
      try {
        // Fetch from nested family members, not the top-level users collection.
        // Querying all users without a where-clause is blocked by Firestore rules.
        const membersSnapshot = await getDocs(collection(db, 'families', familyCode, 'members'));
        const members = [];
        membersSnapshot.forEach((memberDoc) => {
          const memberData = memberDoc.data();
          members.push({
            id: memberDoc.id,
            name: memberData.displayName || `${memberData.firstName || ''} ${memberData.lastName || ''}`.trim() || memberData.email,
            email: memberData.email,
            avatar: memberData.photoURL
          });
        });
        setFamilyMembers(members);
      } catch (error) {
        logger.error('Error loading family members:', error);
      } finally {
        setLoading(false);
      }
    };

    loadFamilyMembers();
  }, [familyCode]);

  // Update parent component when tagged members change
  useEffect(() => {
    if (onChange) {
      onChange(taggedMembers);
    }
  }, [taggedMembers, onChange]);

  const handleTagChange = (event, newValue) => {
    const memberIds = newValue.map(v => v.id);
    setTaggedMembers(memberIds);
  };

  return (
    <Paper sx={{ p: 2, bgcolor: 'background.default', border: '1px solid', borderColor: 'divider' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <TagIcon fontSize="small" color="action" />
        <Typography variant="subtitle2" fontWeight={600}>
          {label}
        </Typography>
      </Box>

      <Autocomplete
        multiple
        options={familyMembers}
        getOptionLabel={(option) => option.name}
        value={familyMembers.filter(m => taggedMembers.includes(m.id))}
        onChange={handleTagChange}
        loading={loading}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder="Start typing to tag members..."
            helperText={helperText}
            size="small"
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <>
                  <PersonIcon fontSize="small" sx={{ ml: 1, color: 'action.active' }} />
                  {params.InputProps.startAdornment}
                </>
              ),
            }}
          />
        )}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <Chip
              icon={<PersonIcon fontSize="small" />}
              label={option.name}
              {...getTagProps({ index })}
              size="small"
              color="primary"
              variant="outlined"
            />
          ))
        }
        renderOption={(props, option) => (
          <Box component="li" {...props}>
            <PersonIcon fontSize="small" sx={{ mr: 1, color: 'action.active' }} />
            <Box>
              <Typography variant="body2">{option.name}</Typography>
              <Typography variant="caption" color="text.secondary">
                {option.email}
              </Typography>
            </Box>
          </Box>
        )}
      />

      {taggedMembers.length > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          {taggedMembers.length} member{taggedMembers.length > 1 ? 's' : ''} will be notified
        </Typography>
      )}
    </Paper>
  );
};

export default TagMembers;
